import type { Location } from '@angular/common';
import type { Router } from '@angular/router';

/**
 * Where a user lands when we have no usable destination to return them to.
 *
 * Deliberately not `/`: that path renders the public marketing landing page
 * (`app.routes.ts`), not the app, so returning a freshly signed-in user there
 * reads as a failed login.
 */
export const DEFAULT_POST_LOGIN_URL = '/wallet';

/**
 * Auth screens: valid places to *be*, never a place to return *to*. Sending a
 * just-authenticated user back to one of these loops them through the flow they
 * have already finished.
 *
 * `/` belongs here too — see DEFAULT_POST_LOGIN_URL — and is handled by the
 * explicit root check in `isReturnableUrl`.
 */
const NON_DESTINATION_PATHS = ['/login', '/forgot-password', '/select-account'];

/**
 * Public pages a background 401 must not evict the user from.
 *
 * A stale token surfacing on the landing page is not a reason to yank someone to
 * the login form; and re-navigating to `/login` while already there would only
 * wipe the query params the current screen is reading (e.g. `sessionExpired`).
 *
 * `/select-account` is deliberately absent: it is a dead end without a session,
 * so a 401 there must still bounce to `/login`.
 */
const STAY_PUT_PATHS = ['/', '/login', '/forgot-password'];

/** The path portion of a router URL, without its query string or fragment. */
function pathOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}

/**
 * The URL the user is actually trying to reach — correct even before the router
 * has committed its first navigation.
 *
 * ## Why `Router.url` cannot be used here
 *
 * `Router.url` is `serializeUrl(currentUrlTree)`, and `currentUrlTree` starts as
 * an empty `UrlTree` that serializes to `"/"`. It is only assigned in the
 * router's `commitTransition()`, which runs at route *activation* — after guards
 * and resolvers. So anything reading `Router.url` earlier in the lifecycle sees
 * `"/"` and not the real target:
 *
 * - from an app initializer, where no navigation has started at all;
 * - from a guard or resolver, where the navigation has not been committed.
 *
 * That is how a forced logout used to produce `/login?returnUrl=%2F` and drop the
 * user on the marketing page after signing back in.
 *
 * The two sources below cover every window: a pending navigation knows its own
 * target, and outside a navigation the browser URL is authoritative (at bootstrap
 * it still holds the deep link the user opened).
 */
export function currentAppUrl(router: Router, location: Location): string {
  const pending = router.getCurrentNavigation();
  if (pending) {
    return router.serializeUrl(pending.finalUrl ?? pending.extractedUrl);
  }

  return location.path() || '/';
}

/**
 * True when `url` is an in-app destination worth returning a user to.
 *
 * Rejects, in order: non-strings and the empty string; anything not rooted at
 * `/` (an absolute `https://…` or a bare `evil.com`); `//host` and `/\host`,
 * which browsers and the router both read as protocol-relative and would take
 * off-origin; the public landing page; and the auth screens themselves.
 */
export function isReturnableUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0) {
    return false;
  }

  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
    return false;
  }

  const path = pathOf(url);
  if (path === '/') {
    return false;
  }

  return !NON_DESTINATION_PATHS.includes(path);
}

/**
 * For *writers*: the value to put in the `returnUrl` query param, or `null` when
 * the param should be omitted entirely so the reader's default applies.
 */
export function toReturnUrlParam(url: unknown): string | null {
  return isReturnableUrl(url) ? url : null;
}

/**
 * For *readers*: a validated destination, falling back to the app's default.
 *
 * Guards the one place a `returnUrl` is handed to `navigateByUrl` — the value
 * arrives from the query string, so it is user input.
 */
export function resolveReturnUrl(raw: unknown, fallback = DEFAULT_POST_LOGIN_URL): string {
  return isReturnableUrl(raw) ? raw : fallback;
}

/**
 * True when a forced logout from `url` should navigate to `/login`.
 *
 * False for the public pages in `STAY_PUT_PATHS`: there the session is cleared
 * in place and the user is left where they are.
 */
export function shouldRedirectToLogin(url: string): boolean {
  return !STAY_PUT_PATHS.includes(pathOf(url));
}
